import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { typography } from '@dixa/lib-shared-tokens';
import last from 'lodash/last';
import pick from 'lodash/pick';
import sum from 'lodash/sum';
import { useCallback } from 'react';
import { Cell, LabelList } from 'recharts';
import { formatInstrumentNumber } from 'app/views/layout/views/analytics/components/panels/utils/formatInstrumentNumber/formatInstrumentNumber';
import { getChartElementColor } from 'app/views/layout/views/analytics/components/panels/utils/theming/theming';
import { tickProps } from '../../../utils/appearance';
/**
 * A hook that returns the props of all series components of a chart.
 *
 * The hook is used to abstract the logic and UI of the chart series as you
 * would usually do with custom components, because the Recharts library does
 * not allow wrapping their chart components into custom components.
 *
 * As of now, chart series can be represented as either bars or lines.
 */
export const useChartInstrumentSeries = ({ instrument, legend, hoveredLegend, hoveredCategory, }) => {
    const { data, layout } = instrument;
    const getLineProps = useCallback(() => {
        const { series } = data;
        const lines = series.map(({ label, options }, index) => {
            var _a;
            const strokeColor = getChartElementColor(index, (_a = data.options) === null || _a === void 0 ? void 0 : _a.theme, options === null || options === void 0 ? void 0 : options.color);
            const isLegendActive = hoveredLegend === null || hoveredLegend === label;
            const line = {
                key: label,
                type: 'monotone',
                dot: false,
                activeDot: { r: 5 },
                dataKey: label,
                stroke: strokeColor,
                strokeWidth: 2,
                strokeOpacity: isLegendActive ? 1 : 0.4,
                connectNulls: true,
                hide: !legend.includes(label),
                animationEasing: 'linear',
            };
            return line;
        });
        return lines;
    }, [data, hoveredLegend, legend]);
    const getBarProps = useCallback(() => {
        const { categories, series } = data;
        const visibleSeries = series.filter(({ label }) => legend.includes(label));
        const isVertical = layout === 'vertical';
        const radius = isVertical
            ? [0, 2, 2, 0]
            : [2, 2, 0, 0];
        const bars = series.map(({ label, options, data: seriesData }, seriesIndex) => {
            var _a, _b;
            const seriesColor = getChartElementColor(seriesIndex, (_a = data.options) === null || _a === void 0 ? void 0 : _a.theme, options === null || options === void 0 ? void 0 : options.color);
            const stackId = options === null || options === void 0 ? void 0 : options.stack;
            const isLastVisible = stackId
                ? ((_b = last(visibleSeries)) === null || _b === void 0 ? void 0 : _b.label) === label
                : true;
            /**
             * The total value for each series is not shown when there are too many
             * series, due to possible lack of space. In stacked charts, the total
             * value is only rendered when the series is the last one in the stack.
             */
            const showSeriesTotal = visibleSeries.length <= 10 && (stackId ? isLastVisible : true);
            const barChildren = (_jsxs(_Fragment, { children: [showSeriesTotal && (_jsx(LabelList, Object.assign({ position: isVertical ? 'right' : 'top', valueAccessor: ({ payload }) => {
                            var _a;
                            // In the case of stacked series, the total sum of all
                            // visible series values for the current category is
                            // displayed. For individual series, their own value is
                            // displayed by using their `label` from the Recharts data.
                            return stackId
                                ? sum(Object.values(pick(payload, legend)))
                                : Number((_a = payload[label]) !== null && _a !== void 0 ? _a : 0);
                        }, formatter: (value) => {
                            var _a;
                            return value
                                ? formatInstrumentNumber(value, (_a = options === null || options === void 0 ? void 0 : options.format) !== null && _a !== void 0 ? _a : 'number')
                                : '';
                        }, offset: 8 }, tickProps, { fontSize: typography.size['100'] }))), seriesData.map((_, categoryIndex) => {
                        var _a, _b, _c, _d;
                        // When using automatic colors, if there are multiple series, the colors are determined
                        // by the series index, resulting in the same color used for a series across categories.
                        const cellColor = series.length > 1
                            ? seriesColor
                            : getChartElementColor(categoryIndex, (_a = data.options) === null || _a === void 0 ? void 0 : _a.theme, 
                            // If there is only one series, the category color is used if specified, otherwise
                            // the colors are determined by the category index, resulting in different colors
                            // being used for each category and repeating as necessary.
                            (_d = (_c = (_b = categories[categoryIndex]) === null || _b === void 0 ? void 0 : _b.options) === null || _c === void 0 ? void 0 : _c.color) !== null && _d !== void 0 ? _d : options === null || options === void 0 ? void 0 : options.color);
                        const isTooltipActive = hoveredCategory === null || hoveredCategory === categoryIndex;
                        const isLegendActive = hoveredLegend === null || hoveredLegend === label;
                        return (_jsx(Cell, { fill: cellColor, fillOpacity: isTooltipActive && isLegendActive ? 1 : 0.4 }, `${label}-${categoryIndex}`));
                    })] }));
            const bar = {
                key: label,
                dataKey: label,
                stackId,
                fill: seriesColor,
                radius: isLastVisible ? radius : 0,
                maxBarSize: 80,
                hide: !legend.includes(label),
                animationEasing: 'linear',
                children: barChildren,
            };
            return bar;
        });
        return bars;
    }, [data, hoveredCategory, hoveredLegend, layout, legend]);
    const getSeriesProps = useCallback((visualization) => {
        if (visualization === 'line') {
            return getLineProps();
        }
        if (visualization === 'bar') {
            return getBarProps();
        }
        return [];
    }, [getBarProps, getLineProps]);
    return { getSeriesProps };
};
